import classNames from 'classnames';
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useMemo } from 'react';
import Icon, { COLORS, ICONS } from '../icon';

interface Props {
	onChange: (tags: string[]) => void;
	defaultTags?: string[];
	label?: string;
	placeholder?: string;
	tooltip?: [string | (() => JSX.Element), string];
	renderTooltip?: () => JSX.Element;
	shouldSearch?: boolean;
	initialTags?: string[];
	dropdownContainerStyles?: { [key: string]: any };
	errorMessage?: string;
	noWhitespace?: boolean;
	maxTags?: number;
	onError?: (err: string) => void;
}

interface TagProps {
	tag: string;
	remove: (tag: string) => void;
}

function Tag(props: TagProps) {
	return useMemo(() => (
		<div className="tag">
			<span>{props.tag} <button className="no-style" onClick={() => props.remove(props.tag)}><Icon name={ICONS.CLOSE} size={10} color={COLORS.DEFAULT_GRAY} /></button></span>
		</div>
	), [props]);
}

//https://brandlive-upload.s3-us-west-2.amazonaws.com/uploads/17/documents/e1s08b9f73/Screen_Shot_2020-11-02_at_9.04.59_AM.png

export default function TagSelectInput(props: Props): JSX.Element {
	const {
		shouldSearch = false,
		defaultTags = [],
		label,
		placeholder,
		tooltip,
		initialTags = [],
		dropdownContainerStyles = {},
		errorMessage,
		noWhitespace = false,
		maxTags,
		onError,
		onChange
	} = props;
	const [tags, setTags] = useState<string[]>(initialTags);
	const [open, setOpen] = useState(false);
	const [entered, setEntered] = useState(false);
	const tagSelector = useRef<any>(null);
	const [image, text] = props.tooltip || [];
	const [searchInput, setSearchInput] = useState("");
	const [visibleTags, setVisibleTags] = useState(defaultTags);

	const itemRefs = useRef<any>(Array.from({ length: visibleTags.length }).map(() => createRef()));

	// on tab or escape, close dropdown
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				['Tab', 'Escape'].includes(event.code) ||
				['Tab', 'Escape'].includes(event.key) ||
				[9, 27].includes(event.keyCode) ||
				[9, 27].includes(event.which)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	const removeTag = useCallback((tag: string) => {
		setTags((tags: string[]) => tags.filter((_tag: string) => _tag !== tag));
	}, []);

	const addTag = useCallback((tag: string) => {
		if (maxTags && tags.length >= maxTags) return onError?.('MAX TAGS');
		tag = tag.trim();

		//make set to ensure that the tags are always unique - will prevent any duplicates
		const tagSet = new Set(tags);
		tagSet.add(tag);
		setTags(Array.from(tagSet.values()));
	}, [maxTags, onError, tags]);

	const handleBlur = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value && !shouldSearch) {
			addTag(e.target.value);
			e.target.value = "";
		} else if (shouldSearch) {
			e.target.value = "";
			setSearchInput("");
		}
	}, [addTag, shouldSearch]);

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;

		//if the input should function as an adder, put the new tag in on Enter key press
		if (!shouldSearch) {
			if (e.key === "Enter") {
				if (target.value) {
					// dont allow empty string and trimed value isnt empty
					(noWhitespace && target.value.trim()) ? addTag(target.value) : addTag(target.value);
					target.value = "";
				}
			}
		} else {
			//if the text input is meant as search, perform search
			setSearchInput(target.value.trim().toLowerCase());
		}
	}, [addTag, noWhitespace, shouldSearch]);

	function handleFocus() {
		setOpen(true);
	}

	useEffect(() => {
		setEntered(tags.length > 0);
		onChange(tags);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags]);

	const handleClick = useCallback((e: any) => {
		const path = e.path || e.composedPath();
		for (const item of path) {
			if (tagSelector.current === item) { return; }
		}
		setOpen(false);
	}, []);

	useEffect(() => {
		if (open) { window.addEventListener("click", handleClick); }

		return () => window.removeEventListener("click", handleClick);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (searchInput) {
			setVisibleTags(
				defaultTags.filter((tag: string) =>
					tag.toLowerCase().includes(searchInput)
				)
			);
		}
		else { setVisibleTags(defaultTags); }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchInput]);

	return (
		<div
			className={classNames("field-group tag-select-container", { entered })}
			ref={tagSelector}
		>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				{label && <label>{label}</label>}
				<div className="tooltip">
					{
						tooltip
							? typeof image === 'function'
								? image()
								: <img src={image} alt="tooltip" height="18px" width="auto" />
							: null
					}{" "} <span className="tooltip-text">{tooltip && text}</span>
				</div>
			</div>
			<div className={classNames("tag-select", { error: errorMessage })}>
				{tags.map((tag: string) => {
					if (noWhitespace) return tag.trim() ? <Tag tag={tag} key={tag} remove={removeTag} /> : null;
					return <Tag tag={tag} key={tag} remove={removeTag} />;
				})}
				<input
					type="text"
					onFocus={handleFocus}
					onBlur={handleBlur}
					placeholder={placeholder}
					onKeyUp={handleKeyUp}
				/>
			</div>
			{errorMessage && <label className="error-message">{errorMessage}</label>}

			{!!defaultTags && (
				<div className={classNames("tag-select-dropdown", { open })} style={dropdownContainerStyles}>
					{visibleTags.map((tag: string, index: number) => (
						<div
							key={tag}
							className="tag-option"
							onClick={() => addTag(tag)}
							ref={itemRefs.current[index]}
						>
							<span>{tag}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}